import { useParams } from 'react-router';
import { SR_PHONE_INVITE_PREFIX } from '@cloudsign/shared';
import { ExternalLink, Smartphone } from 'lucide-react';
import { t } from '../../lib/i18n';
import { PublicShell, Section } from './PublicShell';

/** Android package of the Companion app (docs/modules/companion.md section 8). */
export const COMPANION_PACKAGE = 'uk.cloudsign.app';
export const PLAY_URL = `https://play.google.com/store/apps/details?id=${COMPANION_PACKAGE}`;

/**
 * /c/:token. With the app installed and verified (assetlinks.json), Android opens the app directly and never shows this page.
 * Otherwise this page offers the custom-scheme link (the token is only used there, never sent anywhere) and the Google Play link.
 * It makes no API call except the branding look-up, and it does not use the link, so opening it uses nothing up.
 */
export function CompanionLinkPage() {
  const { token = '' } = useParams();
  // A building marshal's invitation (shared reception, `srm_`) belongs to no company: no branding look-up, and its own wording.
  const marshal = token.startsWith(SR_PHONE_INVITE_PREFIX);
  return (
    <PublicShell token={marshal ? '' : token} title={t(marshal ? 'srb.invite.page.title' : 'publicLinks.app.title')}>
      <Section>
        <div className="flex items-start gap-3">
          <Smartphone aria-hidden="true" className="mt-1 h-6 w-6 shrink-0 text-brand" />
          <p className="text-slate-700">{t(marshal ? 'srb.invite.page.body' : 'publicLinks.app.body')}</p>
        </div>
        <a
          href={`cloudsign://c/${encodeURIComponent(token)}`}
          className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 text-base font-medium text-on-brand hover:bg-brand-hover"
        >
          {t('publicLinks.app.open')}
        </a>
        <p className="text-sm text-slate-600">{t('publicLinks.app.once')}</p>
      </Section>
      <Section>
        <p className="text-sm text-slate-700">{t('publicLinks.app.noApp')}</p>
        <a
          href={PLAY_URL}
          rel="noreferrer noopener"
          referrerPolicy="no-referrer"
          className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-base font-medium text-slate-800 hover:bg-slate-50"
        >
          <ExternalLink aria-hidden="true" className="h-4 w-4" />
          {t('publicLinks.app.store')}
        </a>
      </Section>
    </PublicShell>
  );
}
